/**
 * Human-readable labels for permission keys, in both Hebrew and English.
 * Used by the Roles & Permissions page to display friendly names alongside keys.
 */

export interface PermissionLabelEntry {
    he: string
    en: string
}

type PermissionLabels = Record<string, PermissionLabelEntry>

// Group labels for permission category headers
export const PERMISSION_GROUP_LABELS: PermissionLabels = {
    'access': { he: 'גישה ראשית', en: 'Access' },
    'page': { he: 'עמודים במערכת', en: 'Pages' },
    'users': { he: 'ניהול משתמשים', en: 'User Management' },
    'roles': { he: 'ניהול תפקידים', en: 'Role Management' },
    'permissions': { he: 'ניהול הרשאות', en: 'Permission Management' },
    'sessions': { he: 'ניהול סשנים', en: 'Session Management' },
    'api': { he: 'API', en: 'API' },
    'flutter': { he: 'אפליקציית Flutter', en: 'Flutter App' },
    'module': { he: 'מודולי מכשיר', en: 'Device Modules' },
    'settings': { he: 'הגדרות', en: 'Settings' },
    'settings.ltsp101': { he: 'הגדרות LTSP101', en: 'LTSP101 Settings' },
    'settings.fragmented': { he: 'הגדרות מפוצלות (LTCB220/אחר)', en: 'Fragmented Settings (LTCB220/etc)' },
}

// Individual permission labels
export const PERMISSION_LABELS: PermissionLabels = {
    // ── Access ──
    'access.control_center': { he: 'גישה למערכת הניהול', en: 'Access Control Center' },
    'access.flutter_app': { he: 'גישה לאפליקציית Flutter', en: 'Access Flutter App' },

    // ── Pages ──
    'page.dashboard_internal': { he: 'לוח מכשירים פנימי', en: 'Internal Dashboard' },
    'page.dashboard_external': { he: 'לוח מכשירים חיצוני', en: 'External Dashboard' },
    'page.software_manager': { he: 'ניהול קבצי OTA', en: 'Software Manager' },
    'page.mobile_apps': { he: 'אפליקציות מובייל', en: 'Mobile Apps' },
    'page.usb_flash_artifacts': { he: 'קבצי צריבת USB', en: 'USB Flash Artifacts' },
    'page.usb_flasher_web': { he: 'צריבת USB (דפדפן)', en: 'USB Flasher (Web)' },
    'page.usb_flash_erase_web': { he: 'מחיקת פלאש USB', en: 'USB Flash Erase' },
    'page.usb_serial_monitor_web': { he: 'מוניטור סריאלי USB', en: 'USB Serial Monitor' },
    'page.quicksupport': { he: 'תמיכה מהירה', en: 'Quick Support' },
    'page.ltsp101_catalog': { he: 'קטלוג LTSP101', en: 'LTSP101 Catalog' },
    'page.admin': { he: 'ניהול מערכת', en: 'Admin' },
    'page.users_management': { he: 'ניהול משתמשים והרשאות', en: 'Users & Permissions' },
    'page.roles_permissions': { he: 'תפקידים והרשאות', en: 'Roles & Permissions' },
    'page.connected_devices': { he: 'מכשירים מחוברים', en: 'Connected Devices' },

    // ── Management ──
    'users.read': { he: 'צפייה במשתמשים', en: 'View Users' },
    'users.manage': { he: 'ניהול משתמשים (יצירה/עריכה/מחיקה)', en: 'Manage Users' },
    'roles.read': { he: 'צפייה בתפקידים', en: 'View Roles' },
    'roles.manage': { he: 'ניהול תפקידים', en: 'Manage Roles' },
    'permissions.read': { he: 'צפייה בהרשאות', en: 'View Permissions' },
    'permissions.assign': { he: 'הקצאת הרשאות', en: 'Assign Permissions' },
    'sessions.read_self': { he: 'צפייה בסשנים האישיים', en: 'View Own Sessions' },
    'sessions.revoke_self': { he: 'ביטול סשנים אישיים', en: 'Revoke Own Sessions' },
    'sessions.read_any': { he: 'צפייה בסשנים של אחרים', en: 'View Any Sessions' },
    'sessions.revoke_any': { he: 'ביטול סשנים של אחרים', en: 'Revoke Any Sessions' },

    // ── API ──
    'api.clients.read': { he: 'צפייה בלקוחות מחוברים', en: 'View Connected Clients' },
    'api.admin.manage': { he: 'ניהול מערכת', en: 'Admin Management' },
    'api.mobile_apps.manage': { he: 'ניהול אפליקציות מובייל', en: 'Manage Mobile Apps' },
    'api.software_versions.manage': { he: 'ניהול גרסאות תוכנה', en: 'Manage Software Versions' },
    'api.usb_flash_artifacts.manage': { he: 'ניהול קבצי צריבה', en: 'Manage USB Flash Artifacts' },
    'api.ltsp101_catalog.manage': { he: 'ניהול קטלוג LTSP101', en: 'Manage LTSP101 Catalog' },
    'api.quicksupport.use': { he: 'שימוש בתמיכה מהירה', en: 'Use Quick Support' },

    // ── Flutter ──
    'flutter.machine_lookup': { he: 'חיפוש מכשיר', en: 'Machine Lookup' },
    'flutter.machine': { he: 'מסך מכשיר', en: 'Machine Screen' },
    'flutter.support': { he: 'תמיכה', en: 'Support' },
    'flutter.usb_actions': { he: 'פעולות USB', en: 'USB Actions' },
    'flutter.settings': { he: 'הגדרות', en: 'Settings' },
    'flutter.sessions': { he: 'ניהול סשנים', en: 'Sessions' },

    // ── Settings (parent) ──
    'settings': { he: 'כל הרשאות ההגדרות', en: 'All Settings Permissions' },
    'settings.ltsp101': { he: 'כל הגדרות LTSP101', en: 'All LTSP101 Settings' },
    'settings.fragmented': { he: 'כל ההגדרות המפוצלות', en: 'All Fragmented Settings' },
}

/** Generate label for a permission key — falls back to the key itself if no label exists. */
export function getPermissionLabel(key: string, locale: 'he' | 'en'): string {
    const entry = PERMISSION_LABELS[key]
    if (entry) return entry[locale]
    // Check for prefix match (e.g., "module.0x0002.wificonfig" matches "module")
    const prefix = key.split('.')[0]
    // For settings keys, show the setting path in a readable format
    if (key.startsWith('settings.')) {
        const readable = key
            .replace(/^settings\./, '')
            .replace(/\./g, ' › ')
            .replace(/_/g, ' ')
        return locale === 'he'
            ? readable.replace(/read$/, 'קריאה').replace(/write$/, 'כתיבה')
            : readable.replace(/read$/, 'Read').replace(/write$/, 'Write')
    }
    // For module keys, show the path in a readable format
    if (key.startsWith('module.')) {
        const readable = key.replace(/^module\./, '').replace(/\./g, ' › ').replace(/_/g, ' ')
        return readable
    }
    return key
}

/** Get the group/category name for a permission key. */
export function getPermissionGroup(key: string, locale: 'he' | 'en'): string {
    const parts = key.split('.')
    const topLevel = parts[0]

    // Settings keys get a more specific group
    if (topLevel === 'settings') {
        if (key.startsWith('settings.ltsp101')) {
            const label = PERMISSION_GROUP_LABELS['settings.ltsp101']
            return label ? label[locale] : 'Settings'
        }
        if (key.startsWith('settings.fragmented')) {
            const label = PERMISSION_GROUP_LABELS['settings.fragmented']
            return label ? label[locale] : 'Settings'
        }
        if (key === 'settings') {
            const label = PERMISSION_GROUP_LABELS['settings']
            return label ? label[locale] : 'Settings'
        }
    }

    const label = PERMISSION_GROUP_LABELS[topLevel]
    return label ? label[locale] : topLevel
}

/** Sort order for groups */
export const PERMISSION_GROUP_ORDER: string[] = [
    'access',
    'page',
    'users',
    'roles',
    'permissions',
    'sessions',
    'api',
    'flutter',
    'settings',
    'settings.ltsp101',
    'settings.fragmented',
    'module',
]
